/**
 * DXF block export for sketch objects.
 *
 * Each unique (type, size, params, handedness) combination becomes one BLOCK
 * definition in object-local coordinates; every placed object becomes a single
 * INSERT of it. Repeated sub-glyphs recorded by the builders (cabinet doors,
 * table/desk chairs — see glyphInstances.ts) become their own module blocks,
 * nested as INSERTs inside the object block. Glyph arcs export as true bulged
 * polylines instead of their chord tessellation.
 *
 * Handedness: wall-attached objects on the far side of a wall have a mirrored
 * (left-handed) frame. A mirrored placement gets its own block variant with
 * the mirroring baked into the geometry — INSERT scales stay 1 everywhere, as
 * negative mirror scales flip bulge handedness in some CAD readers.
 */

import type { DxfBlock, DxfEntity, DxfInsertEntity } from '@pointorama/dxf-exporter';
import { getSketchObjectColorProperty } from '@pointorama/potree-shared';
import { hexToColor } from '../../utils/colorUtils';
import type { PointoramaSketchObject } from '../../potree/utils/PointoramaSketchObject';
import type { SketchObjectFrame } from '../../potree/sketchObjects/types';
import { getGlyphArcs, getGlyphInstances, type GlyphArcRecord } from '../../potree/sketchObjects/glyphs/glyphInstances';
import type { Pt2 } from '../../potree/utils/FloorMesh3DBuilder';

type PointConverter = (point: { x: number; y: number; z: number }) => { x: number; y: number; z: number };

/** Rigid local basis: origin + unit X axis; Y is the CCW perpendicular. */
interface LocalBasis {
  origin: Pt2;
  dir: Pt2;
}

function toLocal(basis: LocalBasis, x: number, y: number): Pt2 {
  const dx = x - basis.origin.x;
  const dy = y - basis.origin.y;
  // Right-handed inverse rotation — mirrored child geometry stays baked.
  return {
    x: dx * basis.dir.x + dy * basis.dir.y,
    y: -dx * basis.dir.y + dy * basis.dir.x,
  };
}

function rotationDeg(dir: Pt2): number {
  return (Math.atan2(dir.y, dir.x) * 180) / Math.PI;
}

/** +1 for a right-handed frame, −1 for a mirrored (left-handed) one. */
function frameHandedness(frame: SketchObjectFrame): 1 | -1 {
  return frame.dir.x * frame.norm.y - frame.dir.y * frame.norm.x >= 0 ? 1 : -1;
}

const IDENTITY_BASIS: LocalBasis = { origin: { x: 0, y: 0 }, dir: { x: 1, y: 0 } };

/**
 * Emit block-content entities for segments [from, to) of a flat glyph array,
 * mapped through `basis` into block-local coords: plain LINEs for straight
 * segments, one bulged 2-point polyline per recorded arc (its chords skipped).
 * Entities live on layer '0' with BYBLOCK color so the INSERT drives display.
 */
function emitBlockContent(segments: number[], from: number, to: number, arcs: readonly GlyphArcRecord[], basis: LocalBasis, cp: PointConverter): DxfEntity[] {
  const out: DxfEntity[] = [];
  const localArcs = arcs.filter((a) => a.segStart >= from && a.segEnd <= to);
  const local = (x: number, y: number) => {
    const p = toLocal(basis, x, y);
    return cp({ x: p.x, y: p.y, z: 0 });
  };

  for (let i = from; i + 6 <= to; i += 6) {
    if (localArcs.some((a) => i >= a.segStart && i < a.segEnd)) continue;
    out.push({
      type: 'LINE',
      start: local(segments[i], segments[i + 1]),
      end: local(segments[i + 3], segments[i + 4]),
      colorAci: 0,
      layerName: '0',
    });
  }

  for (const arc of localArcs) {
    if (arc.segEnd - arc.segStart < 6) continue;
    const s = local(segments[arc.segStart], segments[arc.segStart + 1]);
    const p1 = local(segments[arc.segStart + 3], segments[arc.segStart + 4]);
    const e = local(segments[arc.segEnd - 3], segments[arc.segEnd - 2]);
    const c = local(arc.center.x, arc.center.y);
    // Planar sweep direction from the chord geometry (mirroring may have
    // flipped the recorded sign): CCW iff the first chord leaves the start
    // point on the CCW side of the radius. DXF: positive bulge = CCW.
    const ccw = (s.x - c.x) * (p1.y - s.y) - (s.y - c.y) * (p1.x - s.x) > 0;
    const bulge = (ccw ? 1 : -1) * Math.tan(Math.abs(arc.sweep) / 4);
    out.push({
      type: 'POLYLINE',
      points: [s, e],
      closed: false,
      bulges: [bulge],
      colorAci: 0,
      layerName: '0',
    });
  }

  return out;
}

/** One registry per export run — deduplicates blocks across floors. */
export class SketchObjectDxfBlockRegistry {
  private readonly blocks: DxfBlock[] = [];
  private readonly nameByKey = new Map<string, string>();
  private readonly typeCounters = new Map<string, number>();

  getBlocks(): DxfBlock[] {
    return this.blocks;
  }

  /**
   * The block name for this object's geometry, building the block (and its
   * nested module blocks) on first sight of the dedup key.
   */
  ensureObjectBlock(obj: PointoramaSketchObject, frame: SketchObjectFrame, cp: PointConverter): string | null {
    const type = obj.getType();
    if (!type) return null;

    const mm = (v: number) => Math.round(v * 1000);
    const params = type.getParamValues(obj.identifier);
    const paramKey = JSON.stringify(params, Object.keys(params).sort());
    const handedness = frameHandedness(frame);
    const key = `${type.id}|w${mm(frame.width)}|d${mm(frame.depth)}|h${handedness}|${paramKey}`;

    const existing = this.nameByKey.get(key);
    if (existing) return existing;

    // Identity frame with the placement's handedness baked in: the block
    // geometry equals the world glyph up to rotation + translation.
    const identityFrame: SketchObjectFrame = {
      center: { x: 0, y: 0 },
      dir: { x: 1, y: 0 },
      norm: { x: 0, y: handedness },
      width: frame.width,
      depth: frame.depth,
      z: 0,
    };
    const segments = type.buildGlyph(identityFrame, obj.identifier);
    if (segments.length === 0) return null;

    const count = (this.typeCounters.get(type.id) ?? 0) + 1;
    this.typeCounters.set(type.id, count);
    const blockName = `POINTORAMA_${type.id.toUpperCase()}_${count}`;
    this.nameByKey.set(key, blockName);

    const instances = getGlyphInstances(segments);
    const arcs = getGlyphArcs(segments);
    const objectEntities: DxfEntity[] = [];

    // Module blocks: one per unique instance key, geometry from the first
    // occurrence; every occurrence becomes a nested INSERT.
    const moduleNameByKey = new Map<string, string>();
    for (const instance of instances) {
      let moduleName = moduleNameByKey.get(instance.key);
      if (!moduleName) {
        moduleName = `${blockName}_${instance.key.toUpperCase()}`;
        moduleNameByKey.set(instance.key, moduleName);
        const basis: LocalBasis = { origin: instance.frame.center, dir: instance.frame.dir };
        this.blocks.push({ name: moduleName, entities: emitBlockContent(segments, instance.segStart, instance.segEnd, arcs, basis, cp) });
      }
      objectEntities.push({
        type: 'INSERT',
        blockName: moduleName,
        position: cp({ x: instance.frame.center.x, y: instance.frame.center.y, z: 0 }),
        rotation: rotationDeg(instance.frame.dir),
        colorAci: 0,
        layerName: '0',
      });
    }

    // Loose geometry: everything outside the instance ranges.
    const cuts = [...instances].sort((a, b) => a.segStart - b.segStart);
    let cursor = 0;
    for (const instance of cuts) {
      objectEntities.push(...emitBlockContent(segments, cursor, instance.segStart, arcs, IDENTITY_BASIS, cp));
      cursor = instance.segEnd;
    }
    objectEntities.push(...emitBlockContent(segments, cursor, segments.length, arcs, IDENTITY_BASIS, cp));

    this.blocks.push({ name: blockName, entities: objectEntities });
    return blockName;
  }
}

/**
 * A sketch object as a single INSERT of its (shared) block definition, or
 * null when the object must fall back to loose polylines (unknown type,
 * unresolvable frame, or wall-clipped `drawLikeWall` glyphs whose world-space
 * clipping can't live in a reusable block).
 */
export function sketchObjectToDxfInsert(obj: PointoramaSketchObject, layerName: string, cp: PointConverter, registry: SketchObjectDxfBlockRegistry): DxfInsertEntity | null {
  const type = obj.getType();
  if (!type || type.drawLikeWall) return null;
  const frame = obj.resolveFrame();
  if (!frame) return null;

  const blockName = registry.ensureObjectBlock(obj, frame, cp);
  if (!blockName) return null;

  const colorHex = getSketchObjectColorProperty(obj.identifier).value as string;
  return {
    type: 'INSERT',
    blockName,
    position: cp({ x: frame.center.x, y: frame.center.y, z: 0 }),
    rotation: rotationDeg(frame.dir),
    color: colorHex ? hexToColor(colorHex) : undefined,
    layerName,
  };
}
